import { CROP_ORDER, CROPS } from "./catalog";
import { ENEMIES, segmentDistance } from "./enemies";
import { animate, tickAnimation } from "./animation";
import { formation, separate } from "./steering";
import { emptyEquipment, equipmentStats } from "./equipment";
import {
  applyStatus,
  cleanse,
  statusSpeed,
  tickStatuses,
  type StatusId,
} from "./status";
import { AI_PROFILES, chooseBehavior } from "./behavior";
import type { Motion } from "./motion";
import type { SoundName } from "./audio";
import { RESOURCES } from "./resources";
import { EXPEDITIONS, createRun, expeditionResources } from "./expeditions";
import type { ExpeditionId, ExpeditionRun } from "./expeditions";
import { BOSSES, bossMove, bossPhase } from "./bosses";
import {
  createEmptyPlot,
  plantCrop,
  tickPlot,
  clearPlot,
  harvestYield,
  waterPlot,
  fertilizePlot,
} from "./farming";
import { clamp, direction, distance, normalize } from "./math";
import {
  approach,
  CAMP,
  confine,
  GATE,
  newProgress,
  pay,
  REACH,
  RECIPES,
  WELL,
} from "./rules";
import type { Progress, UpgradeId } from "./rules";
import type {
  Companion,
  CompanionCommand,
  CropId,
  Enemy,
  FarmPlot,
  Inventory,
  Player,
  ResourceNode,
  SpoilingFood,
  Vec2,
  Weather,
  ResourceKind,
  EnemyKind,
} from "./types";

export type FarmAction =
  "plant" | "water" | "fertilize" | "harvest" | "awaken" | "sprinkler";
export type Destination =
  | { kind: "plot"; id: number; action?: FarmAction; seed?: CropId }
  | { kind: "objective"; id: string }
  | { kind: "resource"; id: number }
  | { kind: "well" | "camp" | "gate" }
  | { kind: "ground"; pos: Vec2 };
export interface Signal {
  text: string;
  pos?: Vec2;
  color: string;
  sound?: SoundName;
}
export interface Threat {
  kind?: "rush" | "slam" | "volley" | "heal" | "ring";
  /** 环形预警内圈安全，判定与绘制共享该半径。 */
  innerRadius?: number;
  label?: string;
  id: number;
  pos: Vec2;
  remaining: number;
  duration: number;
  radius: number;
  damage: number;
  ranged: boolean;
  source: Vec2;
}
export interface Bolt {
  status?: StatusId;
  kind?: "seed" | "lance" | "spore";
  pierce?: number;
  hitIds?: number[];
  pos: Vec2;
  velocity: Vec2;
  life: number;
  damage: number;
  friendly: boolean;
}
export interface InputFrame {
  move: Vec2;
  attack: boolean;
  aim: Vec2 | null;
}
export interface CombatEffect {
  kind: "ring" | "slash" | "impact" | "awaken" | "dash";
  pos: Vec2;
  angle: number;
  radius: number;
  life: number;
  duration: number;
  color: string;
}
export interface GardenPlot extends FarmPlot {
  sprinkler: boolean;
  lastCrop: CropId | null;
  rotationBonus: boolean;
}

/** 完整玩法模拟不访问 DOM、时钟或存储；同一输入可在 Node 中重放验收。 */
export class World {
  equipment = emptyEquipment();
  guardRemaining = 0;
  private pending: Array<{
    remaining: number;
    actor: Player | Companion;
    resolve: () => void;
  }> = [];
  private dashMotion: {
    actor: Player | Companion;
    direction: Vec2;
    remaining: number;
  } | null = null;
  private rushes: Array<{
    enemy: Enemy;
    source: Vec2;
    end: Vec2;
    age: number;
    radius: number;
    damage: number;
    hit: Set<Player | Companion>;
  }> = [];
  performMotion(
    actor: Player | Companion | Enemy,
    id: Motion,
    duration = 0.5,
    eventAt?: number,
  ) {
    actor.motion = { id, age: 0, duration, eventAt };
  }
  speedOf(actor: Player | Companion | Enemy): number {
    return (
      actor.speed *
      statusSpeed(actor) *
      (actor === this.player ? equipmentStats(this.equipment).speed : 1) *
      (actor === this.controlled && this.guardRemaining > 0 ? 0.55 : 1)
    );
  }
  guard(): boolean {
    if (this.guardRemaining > 0 || statusSpeed(this.controlled) === 0)
      return false;
    this.guardRemaining = 0.85;
    this.performMotion(this.controlled, "guard", 0.85);
    this.say("防御 · 移速降低，伤害减半", "#d3d9b0", undefined, "guard");
    return true;
  }
  inventory: Inventory = {
    water: 8,
    fertilizer: 4,
    food: 5,
    wood: 0,
    stone: 0,
    spiritDew: 3,
    islandDust: 0,
  };
  seeds: Record<CropId, number> = {
    pumpkin: 4,
    coconut: 2,
    wintermelon: 3,
    banana: 4,
    moonherb: 2,
    carrot: 3,
    chili: 2,
  };
  player: Player = {
    pos: { x: 730, y: 545 },
    velocity: { x: 0, y: 0 },
    radius: 22,
    health: 120,
    maxHealth: 120,
    speed: 205,
    facing: -1.57,
    attackCooldown: 0,
    dashCooldown: 0,
    invulnerable: 0,
    flash: 0,
  };
  plots: GardenPlot[] = [];
  companions: Companion[] = [];
  enemies: Enemy[] = [];
  resourceNodes: ResourceNode[] = [];
  spoilingFood: SpoilingFood[] = [];
  threats: Threat[] = [];
  bolts: Bolt[] = [];
  signals: Signal[] = [];
  effects: CombatEffect[] = [];
  ghosts: Array<{
    art: string;
    pos: Vec2;
    age: number;
    duration: number;
    facing: number;
    clip: "death" | "harvest";
  }> = [];
  combo = 0;
  comboRemaining = 0;
  bloomCooldown = 0;
  attackMode: "seed" | "crescent" = "seed";
  scene: "garden" | "expedition" = "garden";
  run: ExpeditionRun | null = null;
  /** 远征只替换可见数组；后台经营与存档继续消费家园引用。 */
  homePlots: GardenPlot[] | null = null;
  homeResourceNodes: ResourceNode[] | null = null;
  homePosition: Vec2 | null = null;
  progress: Progress = newProgress();
  destination: Destination | null = null;
  selectedPlotId: number | null = null;
  selectedCompanionId: number | null = null;
  selectedSeed: CropId = "pumpkin";
  possessedCompanionId: number | null = null;
  possessionRemaining = 0;
  possessionCooldown = 0;
  skillCooldown = 0;
  dashCooldown = 0;
  dodge = 0;
  dayTime = 0.28;
  day = 1;
  elapsed = 0;
  weather: Weather = "clear";
  expedition = false;
  battleLevel = 0;
  battleResult = "";
  nextId = 100;
  private healTick = 0;
  private deathFrame = false;

  constructor() {
    const positions = [
      [626, 358],
      [746, 350],
      [866, 366],
      [978, 399],
      [610, 449],
      [731, 447],
      [851, 461],
      [960, 494],
      [-250, 390],
      [-130, 390],
      [-250, 490],
      [-130, 490],
      [1640, 390],
      [1760, 390],
      [1640, 490],
      [1760, 490],
    ];
    this.plots = positions.map(([x, y], id) => ({
      ...createEmptyPlot(id, x, y),
      sprinkler: false,
      lastCrop: null,
      rotationBonus: false,
    }));
    plantCrop(this.plots[0], "pumpkin");
    Object.assign(this.plots[0], {
      growth: 0.76,
      stage: "flower",
      moisture: 0.36,
      fertility: 0.75,
    });
    const nodes: Array<[ResourceKind, number, number]> = [
      ["tree", 365, 390],
      ["tree", 430, 610],
      ["tree", 1115, 340],
      ["tree", 1240, 505],
      ["tree", 1040, 680],
      ["rock", 335, 530],
      ["rock", 1135, 610],
      ["rock", 970, 285],
      ["rock", 555, 690],
      ["berry", -365, 570],
      ["berry", -40, 630],
      ["berry", 155, 435],
      ["herb", -320, 300],
      ["herb", 90, 320],
      ["herb", 130, 615],
      ["tree", -440, 440],
      ["tree", -120, 700],
      ["tree", -35, 260],
      ["tree", 180, 540],
      ["rock", -385, 650],
      ["amber", 1530, 600],
      ["amber", 1890, 370],
      ["amber", 1960, 600],
      ["rock", 1440, 420],
      ["rock", 1840, 670],
      ["rock", 1710, 270],
      ["herb", 2050, 475],
      ["berry", 1870, 520],
    ];
    this.resourceNodes = nodes.map(([kind, x, y], id) => ({
      id,
      kind,
      pos: { x, y },
      radius: 30,
      health: RESOURCES[kind].health,
      maxHealth: RESOURCES[kind].health,
      active: true,
      respawnTimer: 0,
      sway: id,
    }));
    for (const n of this.resourceNodes) {
      if (
        (Math.abs(n.pos.x - 350) < 115 || Math.abs(n.pos.x - 1250) < 115) &&
        Math.abs(n.pos.y - 485) < 70
      )
        n.pos.y += n.pos.y < 485 ? -80 : 80;
      confine(n.pos);
    }
  }

  get controlled(): Player | Companion {
    return (
      this.companions.find((c) => c.id === this.possessedCompanionId) ??
      this.player
    );
  }
  get mapDefinition() {
    return this.run ? EXPEDITIONS[this.run.mapId] : undefined;
  }
  get boss(): Enemy | undefined {
    return this.enemies.find((e) => e.id === this.run?.bossId);
  }
  get persistentPlots(): GardenPlot[] {
    return this.homePlots ?? this.plots;
  }
  get persistentResources(): ResourceNode[] {
    return this.homeResourceNodes ?? this.resourceNodes;
  }
  get objectiveText(): string {
    if (!this.run) return "照料灵田，准备下一次远行";
    if (this.run.phase === "cleared")
      return "领主已击败 · 从金色出口撤离领取奖励";
    if (this.run.phase === "boss")
      return `${this.mapDefinition!.bossName} · ${this.boss?.boss?.weakness ?? "躲开预警，抓住收招空窗"}`;
    const count = this.run.objectives.filter((o) => o.complete).length;
    return count < 2
      ? `修复路标 ${count}/2 · 先清理守卫，再靠近交互`
      : "封印已解除 · 前往东端遗迹唤醒领主";
  }
  get selectedPlot(): GardenPlot | undefined {
    return this.plots.find((p) => p.id === this.selectedPlotId);
  }
  get selectedCompanion(): Companion | undefined {
    return this.companions.find((c) => c.id === this.selectedCompanionId);
  }
  get companionLimit(): number {
    return 4 + this.progress.capacity * 2;
  }
  get totalHarvests(): number {
    return Object.values(this.progress.harvests).reduce((a, b) => a + b, 0);
  }
  say(
    text: string,
    color = "#e6dfa4",
    pos?: Vec2,
    sound?: Signal["sound"],
  ): void {
    this.signals.push({
      text,
      color,
      pos: pos ? { ...pos } : undefined,
      sound,
    });
  }

  tick(dt: number, input: InputFrame): void {
    dt = clamp(dt, 0, 0.05);
    const previous = new Map(
      [this.player, ...this.companions, ...this.enemies].map((a) => [
        a,
        { ...a.pos },
      ]),
    );
    this.deathFrame = false;
    this.elapsed += dt;
    this.guardRemaining = Math.max(0, this.guardRemaining - dt);
    for (const actor of [this.player, ...this.companions, ...this.enemies])
      tickStatuses(actor, dt, (n) => {
        "kind" in actor ? this.hitEnemy(actor, n) : this.hurt(actor, n);
      });
    for (const action of this.pending) action.remaining -= dt;
    const due = this.pending.filter((a) => a.remaining <= 0);
    this.pending = this.pending.filter((a) => a.remaining > 0);
    for (const action of due)
      if (
        action.actor.health > 0 &&
        !action.actor.statuses?.some((s) => s.id === "freeze")
      )
        action.resolve();
    for (const entity of [
      this.player,
      ...this.companions,
      ...this.enemies,
      ...this.resourceNodes,
      ...this.plots,
    ])
      tickAnimation(entity, dt);
    for (const ghost of this.ghosts) ghost.age += dt;
    this.ghosts = this.ghosts.filter((g) => g.age < g.duration).slice(-32);
    this.dayTime += dt / 300;
    if (this.dayTime >= 1) {
      this.dayTime -= 1;
      this.day++;
    }
    const cycle = this.elapsed % 160;
    this.weather = cycle > 125 ? "rain" : cycle > 95 ? "drizzle" : "clear";
    this.dashCooldown = Math.max(0, this.dashCooldown - dt);
    this.dodge = Math.max(0, this.dodge - dt);
    this.possessionCooldown = Math.max(0, this.possessionCooldown - dt);
    this.skillCooldown = Math.max(0, this.skillCooldown - dt);
    this.bloomCooldown = Math.max(0, this.bloomCooldown - dt);
    this.comboRemaining = Math.max(0, this.comboRemaining - dt);
    if (!this.comboRemaining) this.combo = 0;
    for (const effect of this.effects) effect.life -= dt;
    this.effects = this.effects.filter((effect) => effect.life > 0).slice(-70);
    for (const actor of [this.player, ...this.companions]) {
      actor.attackCooldown = Math.max(0, actor.attackCooldown - dt);
      actor.flash = Math.max(0, actor.flash - dt);
    }
    this.player.invulnerable = Math.max(0, this.player.invulnerable - dt);
    if (this.possessedCompanionId !== null) {
      this.possessionRemaining -= dt;
      if (this.possessionRemaining <= 0) this.endPossession();
    }

    const actor = this.controlled;
    const manual = Math.hypot(input.move.x, input.move.y) > 0.05;
    if (manual) {
      this.destination = null;
      const length = Math.max(1, Math.hypot(input.move.x, input.move.y));
      actor.velocity = {
        x: (input.move.x / length) * this.speedOf(actor),
        y: (input.move.y / length) * this.speedOf(actor),
      };
      actor.pos.x += actor.velocity.x * dt;
      actor.pos.y += actor.velocity.y * dt;
      confine(actor.pos);
      actor.facing = Math.atan2(input.move.y, input.move.x);
    } else {
      actor.velocity = { x: 0, y: 0 };
      this.walkToDestination(dt);
    }
    if (input.aim)
      actor.facing = Math.atan2(
        input.aim.y - actor.pos.y,
        input.aim.x - actor.pos.x,
      );
    if (input.attack) this.attack(input.aim);
    if (
      this.dashMotion &&
      (this.dashMotion.actor !== this.controlled ||
        this.dashMotion.actor.health <= 0 ||
        statusSpeed(this.dashMotion.actor) === 0)
    )
      this.dashMotion = null;
    if (this.dashMotion) {
      const d = this.dashMotion,
        step = (Math.min(dt, d.remaining) * 100) / 0.22;
      d.actor.pos.x += d.direction.x * step;
      d.actor.pos.y += d.direction.y * step;
      confine(d.actor.pos);
      d.remaining -= dt;
      if (d.remaining <= 0) this.dashMotion = null;
    }

    for (const plot of this.persistentPlots) {
      if (plot.sprinkler) plot.moisture = Math.max(plot.moisture, 0.68);
      const result = tickPlot(
        plot,
        dt *
          (1 +
            this.progress.soil * 0.15 +
            (plot.rotationBonus ? 0.12 : 0) +
            this.neighborBonus(plot)),
        this.weather,
      );
      if (result.matured)
        this.say(
          `${CROPS[plot.cropId!].name}成熟了`,
          "#e9e3a3",
          plot.pos,
          "harvest",
        );
    }
    this.updateCompanions(dt);
    separate(this.companions, this.controlled, dt);
    this.updateExpedition();
    this.updateEnemies(dt);
    for (const actor of [this.player, ...this.companions, ...this.enemies]) {
      const from = previous.get(actor);
      if (from && dt > 0)
        actor.velocity = {
          x: (actor.pos.x - from.x) / dt,
          y: (actor.pos.y - from.y) / dt,
        };
      const oldStride = actor.stride ?? 0;
      actor.stride =
        (actor.stride ?? 0) +
        Math.hypot(actor.velocity.x, actor.velocity.y) * dt;
      if (
        actor === this.controlled &&
        Math.floor(oldStride / 30) !== Math.floor(actor.stride / 30)
      )
        this.signals.push({ text: "", color: "", sound: "step" });
      const moving = Math.hypot(actor.velocity.x, actor.velocity.y) > 4;
      if (
        actor.gaitMoving !== undefined &&
        actor.gaitMoving !== moving &&
        !actor.animation &&
        !actor.motion
      )
        this.performMotion(actor, moving ? "start" : "stop", 0.16);
      actor.gaitMoving = moving;
      if (
        "cropId" in actor &&
        actor !== this.controlled &&
        !actor.animation &&
        Math.hypot(actor.velocity.x, actor.velocity.y) > 4
      )
        actor.facing = Math.atan2(actor.velocity.y, actor.velocity.x);
    }
    this.updateThreats(dt);
    for (const rush of this.rushes) {
      if (
        rush.enemy.health <= 0 ||
        rush.enemy.stagger ||
        statusSpeed(rush.enemy) === 0
      ) {
        rush.age = 0.24;
        continue;
      }
      const previous = { ...rush.enemy.pos };
      rush.age += dt;
      const p = Math.min(1, rush.age / 0.24);
      rush.enemy.pos = {
        x: rush.source.x + (rush.end.x - rush.source.x) * p,
        y: rush.source.y + (rush.end.y - rush.source.y) * p,
      };
      confine(rush.enemy.pos);
      for (const a of [this.player, ...this.companions])
        if (
          !rush.hit.has(a) &&
          segmentDistance(a.pos, previous, rush.enemy.pos) < rush.radius
        ) {
          rush.hit.add(a);
          this.hurt(a, rush.damage);
        }
    }
    this.rushes = this.rushes.filter((r) => r.age < 0.24 && r.enemy.health > 0);
    for (const node of this.persistentResources)
      if (!node.active) {
        node.respawnTimer -= dt;
        if (node.respawnTimer <= 0) {
          node.active = true;
          node.health = node.maxHealth;
        }
      }
    for (const food of this.spoilingFood) food.remaining -= dt;
    this.spoilingFood = this.spoilingFood.filter(
      (f) => f.remaining > 0 && f.amount > 0,
    );
    this.healTick += dt;
    if (this.healTick >= 1) {
      this.healTick = 0;
      if (!this.expedition) {
        this.player.health = Math.min(
          this.player.maxHealth,
          this.player.health + 3,
        );
        for (const c of this.companions)
          c.health = Math.min(c.maxHealth, c.health + 2);
      }
    }
    if (!this.deathFrame) this.updateExpedition();
  }

  neighborBonus(plot: FarmPlot): number {
    return this.persistentPlots.some(
      (p) =>
        p.id !== plot.id &&
        p.cropId &&
        p.cropId !== plot.cropId &&
        distance(p.pos, plot.pos) < 135,
    )
      ? 0.1
      : 0;
  }

  targetPosition(target = this.destination): Vec2 | null {
    if (!target) return null;
    if (target.kind === "plot")
      return this.plots.find((p) => p.id === target.id)?.pos ?? null;
    if (target.kind === "resource")
      return (
        this.resourceNodes.find((n) => n.id === target.id && n.active)?.pos ??
        null
      );
    if (target.kind === "ground") return target.pos;
    if (target.kind === "objective")
      return target.id === "exit"
        ? (this.run?.exit ?? null)
        : (this.run?.objectives.find((o) => o.id === target.id)?.pos ?? null);
    return target.kind === "well" ? WELL : target.kind === "camp" ? CAMP : GATE;
  }

  go(target: Destination): void {
    if (
      this.scene === "expedition" &&
      ["plot", "well", "camp", "gate"].includes(target.kind)
    )
      return;
    this.destination = target;
    if (target.kind === "plot") this.selectedPlotId = target.id;
  }
  requestFarm(
    action: FarmAction,
    id = this.selectedPlotId,
    seed = this.selectedSeed,
  ): void {
    if (id === null) return;
    if (this.scene !== "garden" || !this.plots.some((p) => p.id === id)) {
      this.say("灵田在庭园中，先撤离再照料");
      return;
    }
    if (this.possessedCompanionId !== null) {
      this.say("先结束附身再照料灵田");
      return;
    }
    this.go({ kind: "plot", id, action, seed });
    if (distance(this.player.pos, this.targetPosition()!) <= REACH)
      this.walkToDestination(0.016);
  }

  private walkToDestination(dt: number): void {
    const target = this.destination,
      pos = this.targetPosition();
    if (!target || !pos) {
      this.destination = null;
      return;
    }
    const actor = this.controlled,
      stop =
        target.kind === "ground" ? 4 : target.kind === "resource" ? 68 : REACH;
    if (distance(actor.pos, pos) > stop + 2) {
      actor.velocity = approach(actor.pos, pos, this.speedOf(actor), dt, stop);
      actor.facing = Math.atan2(pos.y - actor.pos.y, pos.x - actor.pos.x);
      return;
    }
    if (target.kind === "resource") {
      const node = this.resourceNodes.find(
        (n) => n.id === target.id && n.active,
      );
      if (node) this.chop(node, actor);
      else this.destination = null;
      return;
    }
    this.destination = null;
    if (target.kind === "plot" && target.action)
      this.farm(target.id, target.action, target.seed);
    if (target.kind === "objective") this.interactExpedition(target.id);
    if (target.kind === "well") {
      this.inventory.water = 12;
      this.say("水壶已装满 · 泉水可以反复取用", "#a6dfda", WELL, "water");
    }
    if (target.kind === "camp") {
      this.player.health = this.player.maxHealth;
      this.say("营地休息 · 生命恢复", "#b4e391", CAMP, "collect");
    }
  }

  farm(id: number, action: FarmAction, seed = this.selectedSeed): boolean {
    const p = this.plots.find((p) => p.id === id);
    if (!p || this.possessedCompanionId !== null) return false;
    if (distance(this.player.pos, p.pos) > REACH + 3) return false;
    if (this.expedition) return false;
    this.player.facing = Math.atan2(
      p.pos.y - this.player.pos.y,
      p.pos.x - this.player.pos.x,
    );
    if (action === "sprinkler") return this.upgrade("sprinkler", p.id);
    if (action === "plant") {
      if (p.cropId || this.seeds[seed] < 1) {
        this.say("种子不足，可到行囊换种");
        return false;
      }
      p.rotationBonus = p.lastCrop !== null && p.lastCrop !== seed;
      plantCrop(p, seed);
      animate(this.player, "plant");
      this.seeds[seed]--;
      p.fertility = Math.max(p.fertility, 0.58 + this.progress.soil * 0.1);
      this.say(
        `种下${CROPS[seed].name}${p.rotationBonus ? " · 轮作 +12%" : ""}`,
        "#cee997",
        p.pos,
        "plant",
      );
      return true;
    }
    if (!p.cropId) return false;
    if (action === "water") {
      const ok = waterPlot(p, this.inventory);
      if (ok) {
        animate(this.player, "water");
        this.progress.watered++;
        this.say("水分 +48%", "#a7dedd", p.pos, "water");
      } else
        this.say(
          this.inventory.water < 1
            ? "水壶空了，点击「取水」自动前往泉边"
            : "土壤已经足够湿润",
        );
      return ok;
    }
    if (action === "fertilize") {
      const ok = fertilizePlot(p, this.inventory);
      if (ok) animate(this.player, "plant");
      this.say(
        ok ? "肥力 +44%" : "肥料不足或土壤已肥沃，可在行囊堆肥",
        "#e4cf8a",
        ok ? p.pos : undefined,
        ok ? "plant" : undefined,
      );
      return ok;
    }
    if (p.stage !== "mature") {
      this.say("成熟后才能收获或唤灵");
      return false;
    }
    const crop = CROPS[p.cropId];
    if (action === "harvest") {
      animate(this.player, "gather");
      this.performMotion(this.player, "harvest", 0.65);
      this.ghosts.push({
        art: `crop-${p.cropId}`,
        pos: { ...p.pos },
        age: 0,
        duration: 8 / 12,
        facing: 0,
        clip: "harvest",
      });
      const reward = harvestYield(p);
      this.inventory.food += reward.food;
      this.seeds[p.cropId] += reward.seeds;
      this.inventory.spiritDew += reward.bonusDew;
      this.progress.harvests[p.cropId]++;
      this.progress.coins += 2 + Math.floor(p.quality * 3);
      this.say(
        `收获：食物 +${reward.food} / 种子 +${reward.seeds} / 铜叶 +${2 + Math.floor(p.quality * 3)}`,
        "#f2de9a",
        p.pos,
        "harvest",
      );
    } else {
      if (this.companions.length >= this.companionLimit) {
        this.say("灵契已满，在工坊扩容后再唤醒");
        return false;
      }
      if (!pay(this.inventory, { spiritDew: crop.awakenCost })) {
        this.say("灵露不足，可完成委托或收获月露草");
        return false;
      }
      const hp = Math.round(crop.companionHp * (0.85 + p.quality * 0.3));
      const c: Companion = {
        id: this.nextId++,
        cropId: p.cropId,
        name: crop.companionName,
        pos: { x: p.pos.x, y: p.pos.y + 28 },
        velocity: { x: 0, y: 0 },
        radius: 22,
        health: hp,
        maxHealth: hp,
        speed: crop.companionSpeed * 1.35,
        damage: Math.round(crop.companionDamage * (0.85 + p.quality * 0.3)),
        facing: 1.57,
        attackCooldown: 0,
        gatherCooldown: 0,
        producedTimer: 0,
        flash: 0,
        command: "follow",
        anchor: { ...p.pos },
        focusEnemyId: null,
        reviveWard: false,
        possessed: false,
      };
      this.companions.push(c);
      this.performMotion(c, "cheer", 1);
      animate(this.player, "cast");
      this.selectedCompanionId = c.id;
      this.progress.awakened++;
      this.effect("awaken", p.pos, 110, "#eddf9a", 1.6);
      this.seeds[p.cropId]++;
      this.say(
        `${crop.companionName}醒来了 · 留种 +1`,
        "#e4d591",
        p.pos,
        "awaken",
      );
    }
    p.lastCrop = p.cropId;
    clearPlot(p);
    return true;
  }

  upgrade(id: UpgradeId, plotId = this.selectedPlotId): boolean {
    const recipe = RECIPES[id],
      plot = this.plots.find((p) => p.id === plotId);
    const level =
      id === "sprinkler" ? (plot?.sprinkler ? 1 : 0) : this.progress[id];
    if (id === "sprinkler" && !plot) {
      this.say("先选择需要滴灌的灵田");
      return false;
    }
    if (level >= recipe.max) {
      this.say("已达到最高等级");
      return false;
    }
    if (!pay(this.inventory, recipe.cost, level + 1)) {
      this.say("材料不足，点击树木或岩石可自动采集");
      return false;
    }
    if (id === "sprinkler") plot!.sprinkler = true;
    else this.progress[id]++;
    this.progress.crafted++;
    this.performMotion(this.player, "craft", 1);
    this.say(`${recipe.name}已完成`, "#d5e8a0", undefined, "craft");
    return true;
  }

  buySeed(crop: CropId): boolean {
    const price = crop === "moonherb" ? 4 : 2;
    if (this.progress.coins < price) {
      this.say("铜叶不足，收获或完成委托可以获得");
      return false;
    }
    this.progress.coins -= price;
    this.seeds[crop] += 2;
    this.say(`${CROPS[crop].seedName} +2`);
    return true;
  }
  starterSeeds(): boolean {
    if (
      Object.values(this.seeds).some((n) => n > 0) ||
      this.plots.some((p) => p.cropId)
    ) {
      this.say("仍有种子或作物，先种植收获即可循环");
      return false;
    }
    this.seeds.pumpkin = 2;
    this.say("庭园赠予南瓜种子 ×2，重新开始种植");
    return true;
  }
  compost(): boolean {
    if (!pay(this.inventory, { wood: 2 })) {
      this.say("堆肥需要木材 ×2");
      return false;
    }
    this.inventory.fertilizer += 3;
    this.say("木材堆肥：肥料 +3");
    return true;
  }
  distill(): boolean {
    if (!pay(this.inventory, { food: 4 })) {
      this.say("酿造灵露需要食物 ×4");
      return false;
    }
    this.inventory.spiritDew++;
    this.say("作物精华凝结为灵露 +1");
    return true;
  }

  orderCrop(): CropId {
    return CROP_ORDER[this.progress.order % CROP_ORDER.length];
  }
  orderReady(): boolean {
    return (
      this.progress.harvests[this.orderCrop()] > this.progress.orderBaseline &&
      this.inventory.food >= 3
    );
  }
  deliver(): boolean {
    if (!this.orderReady()) {
      this.say("先收获委托要求的作物，并准备食物 ×3");
      return false;
    }
    this.inventory.food -= 3;
    this.inventory.spiritDew += 2;
    this.inventory.fertilizer += 2;
    this.progress.coins += 12;
    this.progress.order++;
    this.progress.orderBaseline = this.progress.harvests[this.orderCrop()];
    this.say(
      "委托完成：铜叶 +12 / 灵露 +2 / 肥料 +2",
      "#f1de9d",
      undefined,
      "collect",
    );
    return true;
  }

  questInfo(): {
    title: string;
    detail: string;
    ready: boolean;
    current: number;
    target: number;
  } {
    const p = this.progress;
    const stages = [
      ["照料第一株南瓜", "点灵田 → 浇水。角色会自动走近。", p.watered, 1],
      [
        "把成熟作物带回家",
        "南瓜成熟后收获，得到食物、种子和铜叶。",
        this.totalHarvests,
        1,
      ],
      [
        "把种子养成伙伴",
        "种植 → 成熟 → 唤灵。保留种子，建立自己的小队。",
        p.awakened,
        1,
      ],
      [
        "木石变成新设施",
        "点击树或石自动采集，在工坊制作任意设施。",
        p.crafted,
        1,
      ],
      [
        "第一次雾境试炼",
        "准备好后点「雾境」，召集伙伴主动挑战。",
        p.victories,
        1,
      ],
      [
        "让青屿繁盛",
        "收获 8 次并赢得 3 场试炼。目标完成后仍可继续经营。",
        Math.min(this.totalHarvests, 8) + Math.min(p.victories, 3),
        11,
      ],
    ] as const;
    if (p.quest >= stages.length)
      return {
        title: "青屿已繁盛",
        detail: "所有旅程目标达成。继续培育、建设、挑战更强的雾境。",
        ready: false,
        current: 1,
        target: 1,
      };
    const [title, detail, current, target] = stages[p.quest];
    return { title, detail, current, target, ready: current >= target };
  }
  claimQuest(): boolean {
    if (!this.questInfo().ready) return false;
    this.progress.quest++;
    this.progress.coins += 8;
    this.inventory.spiritDew++;
    this.inventory.fertilizer += 2;
    this.say(
      "旅程奖励：铜叶 +8 / 灵露 +1 / 肥料 +2",
      "#ecdc8d",
      undefined,
      "harvest",
    );
    return true;
  }

  interact(): void {
    this.performMotion(this.controlled, "interact", 0.6);
    if (this.expedition) {
      if (!this.interactExpedition()) {
        const node = this.resourceNodes
          .filter((n) => n.active && distance(n.pos, this.controlled.pos) < 110)
          .sort(
            (a, b) =>
              distance(a.pos, this.controlled.pos) -
              distance(b.pos, this.controlled.pos),
          )[0];
        if (node) this.go({ kind: "resource", id: node.id });
        else this.say(this.objectiveText);
      }
      return;
    }
    if (distance(this.controlled.pos, WELL) < REACH) {
      this.go({ kind: "well" });
      return;
    }
    const nearby = [...this.plots].sort(
      (a, b) =>
        distance(a.pos, this.controlled.pos) -
        distance(b.pos, this.controlled.pos),
    )[0];
    if (nearby && distance(nearby.pos, this.controlled.pos) < REACH + 20) {
      this.selectedPlotId = nearby.id;
      // 交互只选择，收获与唤醒必须由玩家明确决定，避免一键误收成熟灵植。
      this.go({ kind: "plot", id: nearby.id });
      return;
    }
    const node = this.resourceNodes
      .filter((n) => n.active)
      .sort(
        (a, b) =>
          distance(a.pos, this.controlled.pos) -
          distance(b.pos, this.controlled.pos),
      )[0];
    if (node && distance(node.pos, this.controlled.pos) < 140) {
      this.go({ kind: "resource", id: node.id });
      return;
    }
    this.say("点选灵田或资源，角色会自动前往");
  }

  private chop(node: ResourceNode, actor: Player | Companion): void {
    if (actor.attackCooldown > 0 || !node.active || statusSpeed(actor) === 0)
      return;
    actor.attackCooldown = 0.42;
    animate(actor, "gather");
    this.performMotion(
      actor,
      node.kind === "tree"
        ? "chop"
        : node.kind === "rock" || node.kind === "amber"
          ? "mine"
          : "gather",
      0.42,
      0.25,
    );

    actor.facing = Math.atan2(
      node.pos.y - actor.pos.y,
      node.pos.x - actor.pos.x,
    );
    const damage =
      "cropId" in actor
        ? CROPS[actor.cropId].workKind === node.kind
          ? 55
          : 22
        : 26 + this.progress.tools * 10;
    const resolve = () => {
      if (!node.active || distance(actor.pos, node.pos) > 125) return;
      animate(node, "hurt");
      node.health -= damage;
      this.say(
        `-${damage}`,
        node.kind === "tree" ? "#acc983" : "#c1cac1",
        node.pos,
        node.kind === "tree"
          ? "chop"
          : node.kind === "rock" || node.kind === "amber"
            ? "mine"
            : "collect",
      );
      if (node.health <= 0) {
        node.active = false;
        const spec = RESOURCES[node.kind];
        node.respawnTimer = spec.respawn;
        for (const [key, amount] of Object.entries(spec.rewards))
          this.inventory[key as keyof Inventory] += amount;
        this.ghosts.push({
          art: node.kind,
          pos: { ...node.pos },
          age: 0,
          duration: 8 / 12,
          facing: 0,
          clip: "harvest",
        });
        this.progress.gathered++;
        if (this.run) this.run.gathered++;
        this.say(`${spec.name} · ${spec.note}`, "#e5d5a2", node.pos, "collect");
      }
    };
    this.pending.push({ actor, remaining: 0.25, resolve });
  }

  attack(aim: Vec2 | null = null): void {
    const actor = this.controlled;
    if (actor.attackCooldown > 0 || statusSpeed(actor) === 0) return;
    // 触屏攻击自动选择近敌；点选采集的明确命令优先由移动任务执行。
    const candidates = this.enemies.filter(
      (e) =>
        e.health > 0 &&
        distance(actor.pos, e.pos) <
          (actor === this.player ||
          ("cropId" in actor && ["carrot", "chili"].includes(actor.cropId))
            ? 300
            : 125),
    );
    const target = candidates.sort(
      (a, b) =>
        distance(a.pos, aim ?? actor.pos) - distance(b.pos, aim ?? actor.pos),
    )[0];
    if (!target) {
      const node = this.resourceNodes
        .filter((n) => n.active && distance(n.pos, actor.pos) < 112)
        .sort(
          (a, b) => distance(a.pos, actor.pos) - distance(b.pos, actor.pos),
        )[0];
      if (node) {
        this.chop(node, actor);
        return;
      }
    }
    actor.attackCooldown = actor === this.player ? 0.3 : 0.52;
    animate(actor, "attack");
    this.performMotion(actor, "attack", 0.4, 4 / 30);
    const to = target?.pos ??
      aim ?? {
        x: actor.pos.x + Math.cos(actor.facing) * 100,
        y: actor.pos.y + Math.sin(actor.facing) * 100,
      };
    actor.facing = Math.atan2(to.y - actor.pos.y, to.x - actor.pos.x);
    const resolve = () => {
      if (actor === this.player) {
        this.combo = (this.combo % 3) + 1;
        this.comboRemaining = 1.1;
        const finisher = this.combo === 3;
        if (this.attackMode === "crescent") {
          actor.attackCooldown = finisher ? 0.65 : 0.38;
          const radius = finisher ? 185 : 150;
          this.effect(
            "slash",
            actor.pos,
            radius,
            finisher ? "#f9d399" : "#b8d1ff",
            0.4,
            actor.facing,
          );
          for (const enemy of [...this.enemies]) {
            const d = direction(actor.pos, enemy.pos);
            if (
              distance(actor.pos, enemy.pos) > radius + enemy.radius ||
              d.x * Math.cos(actor.facing) + d.y * Math.sin(actor.facing) < 0.2
            )
              continue;
            this.hitEnemy(
              enemy,
              (finisher ? 52 : 28) + this.progress.tools * 8,
              actor.pos,
            );
            if (finisher && !enemy.boss) {
              enemy.pos.x += d.x * 35;
              enemy.pos.y += d.y * 35;
              confine(enemy.pos);
              enemy.stagger = 0.3;
            }
          }
          this.say(finisher ? "月刃·破风" : "", "#d2dcff", actor.pos, "lance");
          return;
        }
        this.signals.push({
          text: "",
          color: "",
          sound: finisher ? "lance" : "cast",
        });
        if (finisher) actor.attackCooldown = 0.55;
        const d = direction(actor.pos, to);
        this.effect(
          "slash",
          actor.pos,
          finisher ? 95 : 60,
          finisher ? "#ffe8a3" : "#92f8d1",
          0.28,
          actor.facing,
        );
        this.bolts.push({
          kind: finisher ? "lance" : "seed",
          pierce: finisher ? 3 : 1,
          hitIds: [],
          pos: { ...actor.pos },
          velocity: { x: d.x * 590, y: d.y * 590 },
          life: 0.62,
          damage:
            ((finisher ? 40 : 21) + this.progress.tools * 7) *
            equipmentStats(this.equipment).power,
          friendly: true,
          status: equipmentStats(this.equipment).element,
        });
      } else if (
        "cropId" in actor &&
        (actor.cropId === "carrot" || actor.cropId === "chili")
      ) {
        const d = direction(actor.pos, to);
        this.bolts.push({
          kind: "seed",
          pos: { ...actor.pos },
          velocity: { x: d.x * 480, y: d.y * 480 },
          life: 0.8,
          damage: actor.damage * 1.25,
          friendly: true,
        });
      } else if (target)
        this.hitEnemy(target, (actor as Companion).damage * 1.25, actor.pos);
    };
    this.pending.push({ remaining: 4 / 30, actor, resolve });
  }

  dash(): boolean {
    if (this.dashCooldown > 0 || statusSpeed(this.controlled) === 0)
      return false;
    this.dashCooldown = 1.2;
    this.dodge = 0.32;
    const a = this.controlled;
    // 新行仅导出在主角图集中；附身保留伙伴已有动作，不能采样不存在的行。
    animate(a, a === this.player ? "dash" : "attack");
    this.performMotion(a, "dash", 0.33);
    this.effect("dash", a.pos, 100, "#abffe1", 0.4, a.facing);
    let d = normalize(a.velocity);
    if (!Math.hypot(d.x, d.y))
      d = { x: Math.cos(a.facing), y: Math.sin(a.facing) };
    this.dashMotion = { actor: a, direction: d, remaining: 0.22 };
    this.destination = null;
    this.say("闪避", "#c9e8a8", a.pos, "dash");
    return true;
  }

  cycleAttackMode(): void {
    this.attackMode = this.attackMode === "seed" ? "crescent" : "seed";
    this.combo = 0;
    this.comboRemaining = 0;
    this.say(
      this.attackMode === "seed"
        ? "灵种式 · 远程三连与穿透"
        : "月刃式 · 近身扇斩与破风",
      "#c8dfff",
      undefined,
      "ui",
    );
  }

  possess(): boolean {
    if (this.possessedCompanionId !== null) {
      this.endPossession();
      return true;
    }
    const c = this.selectedCompanion;
    if (!c) {
      this.say("先唤醒并选择一位伙伴");
      return false;
    }
    if (this.possessionCooldown > 0) {
      this.say(`灵识恢复中 · ${Math.ceil(this.possessionCooldown)} 秒`);
      return false;
    }
    this.possessedCompanionId = c.id;
    c.possessed = true;
    this.possessionRemaining = 12;
    this.destination = null;
    this.skillCooldown = 0;
    this.say(
      `附身 ${c.name} · 使用「灵技」施展专属能力`,
      "#e5dca1",
      c.pos,
      "awaken",
    );
    return true;
  }
  endPossession(): void {
    for (const c of this.companions) c.possessed = false;
    this.possessedCompanionId = null;
    this.possessionRemaining = 0;
    this.possessionCooldown = 8;
    this.destination = null;
  }
  skill(): boolean {
    const c = this.controlled;
    if (statusSpeed(c) === 0) return false;
    if (!("cropId" in c)) {
      if (this.bloomCooldown > 0) return false;
      this.bloomCooldown = 6;
      animate(c, "cast");
      this.performMotion(c, "cast", 0.5, 0.1875);
      const resolve = () => {
        this.effect("ring", c.pos, 215, "#b2ffe0", 0.65);
        for (const e of [...this.enemies]) {
          if (distance(e.pos, c.pos) > 215) continue;
          this.hitEnemy(e, 38 + this.progress.tools * 6);
          const d = direction(c.pos, e.pos);
          e.pos.x += d.x * 65;
          e.pos.y += d.y * 65;
          confine(e.pos);
          e.stagger = 0.8;
          this.threats = this.threats.filter((t) => t.id !== e.id);
          if (e.boss) {
            e.recovery = 2.4;
            e.attackCooldown = 2.4;
            e.boss.weakness = "蓄力被打断 · 核心暴露，伤害 ×1.8";
          }
        }
        this.say("灵潮绽放 · 击退并打断蓄力", "#b2ffe0", c.pos, "awaken");
      };
      this.pending.push({ actor: c, remaining: 0.1875, resolve });
      return true;
    }
    if (this.skillCooldown > 0) return false;
    this.skillCooldown = 4;
    animate(c, "attack");
    this.performMotion(
      c,
      c.cropId === "moonherb"
        ? "heal"
        : c.cropId === "wintermelon"
          ? "slam"
          : "cast",
      0.6,
      0.18,
    );
    const resolve = () => {
      this.effect(
        "ring",
        c.pos,
        c.cropId === "moonherb" ? 220 : 180,
        CROPS[c.cropId].secondary,
        0.7,
      );
      if (c.cropId === "carrot") {
        const d = { x: Math.cos(c.facing), y: Math.sin(c.facing) };
        this.bolts.push({
          kind: "lance",
          pos: { ...c.pos },
          velocity: { x: d.x * 630, y: d.y * 630 },
          life: 1,
          damage: c.damage * 3,
          friendly: true,
          pierce: 5,
          hitIds: [],
        });
      } else if (c.cropId === "chili") {
        for (const e of [...this.enemies])
          if (distance(e.pos, c.pos) < 240) {
            e.stagger = 1.5;
            this.hitEnemy(e, c.damage * 2.4);
            applyStatus(e, "burn");
            this.threats = this.threats.filter((t) => t.id !== e.id);
            if (e.boss) {
              e.recovery = 2.4;
              e.attackCooldown = 2.4;
            }
          }
        this.effect("ring", c.pos, 240, "#ffa568", 0.8);
      } else if (c.cropId === "moonherb") {
        for (const a of [this.player, ...this.companions])
          if (distance(a.pos, c.pos) < 220) {
            a.health = Math.min(a.maxHealth, a.health + 45);
            cleanse(a);
          }
      } else if (c.cropId === "coconut") {
        c.health = Math.min(c.maxHealth, c.health + 45);
        cleanse(c);
        for (const e of this.enemies)
          if (distance(e.pos, c.pos) < 210) this.hitEnemy(e, 25);
      } else {
        const radius = c.cropId === "banana" ? 155 : 180;
        for (const e of this.enemies)
          if (distance(e.pos, c.pos) < radius) {
            this.hitEnemy(e, c.damage * 2.2);
            if (c.cropId === "wintermelon") applyStatus(e, "freeze");
            if (c.cropId === "banana") applyStatus(e, "slow");
            if (c.cropId === "pumpkin") e.stagger = 0.7;
          }
        for (const n of this.resourceNodes)
          if (
            n.active &&
            distance(n.pos, c.pos) < radius &&
            CROPS[c.cropId].workKind === n.kind
          ) {
            c.attackCooldown = 0;
            this.chop(n, c);
          }
      }
      this.say(
        c.cropId === "carrot"
          ? "穿林根矢"
          : c.cropId === "chili"
            ? "焰心绽放"
            : c.cropId === "moonherb"
              ? "月华疗愈"
              : c.cropId === "coconut"
                ? "椰露冲击"
                : c.cropId === "banana"
                  ? "风叶回旋"
                  : c.cropId === "wintermelon"
                    ? "磐石震地"
                    : "藤斧横扫",
        "#e5e5ae",
        c.pos,
        "awaken",
      );
    };
    this.pending.push({ actor: c, remaining: 0.18, resolve });
    return true;
  }

  command(command: CompanionCommand, all = false): void {
    const focus = [...this.enemies].sort(
      (a, b) =>
        distance(a.pos, this.controlled.pos) -
        distance(b.pos, this.controlled.pos),
    )[0];
    for (const c of all
      ? this.companions
      : this.selectedCompanion
        ? [this.selectedCompanion]
        : []) {
      c.command = command;
      c.anchor = { ...c.pos };
      c.focusEnemyId = focus?.id ?? null;
      if (!this.enemies.length) this.performMotion(c, "social", 0.65);
    }
  }
  nextCompanion(): void {
    const i = this.companions.findIndex(
      (c) => c.id === this.selectedCompanionId,
    );
    this.selectedCompanionId =
      this.companions[(i + 1) % Math.max(1, this.companions.length)]?.id ??
      null;
  }
  ward(): boolean {
    const c = this.selectedCompanion;
    if (!c || c.reviveWard) return false;
    if (!pay(this.inventory, { spiritDew: 2 })) {
      this.say("续生印需要灵露 ×2");
      return false;
    }
    c.reviveWard = true;
    this.say(`${c.name} 获得一次续生机会`);
    return true;
  }
  eat(): boolean {
    const a = this.controlled;
    if (a.health >= a.maxHealth) {
      this.say("生命已满");
      return false;
    }
    const spoil = this.spoilingFood.find(
      (f) => f.remaining > 0 && f.amount > 0,
    );
    const heal = spoil ? 14 : 32;
    if (spoil) spoil.amount--;
    else if (!pay(this.inventory, { food: 1 })) {
      this.say("食物不足");
      return false;
    }
    a.health = Math.min(a.maxHealth, a.health + heal);
    this.performMotion(a, "eat", 1);
    if (this.equipment.slots.charm === "dewCharm") cleanse(a);
    this.say(
      `恢复 ${heal}${spoil ? " · 易腐食物" : ""}`,
      "#b9e6a0",
      a.pos,
      "collect",
    );
    return true;
  }

  startExpedition(mapId: ExpeditionId = "verdant-ruins"): boolean {
    if (this.expedition) return false;
    if (!Object.hasOwn(EXPEDITIONS, mapId)) return false;
    if (this.progress.awakened < 1) {
      this.say("先培育第一位伙伴，再一起进入雾境");
      return false;
    }
    this.battleLevel = mapId === "verdant-ruins" ? 1 : 2;
    this.pending = [];
    this.rushes = [];
    this.dashMotion = null;
    this.expedition = true;
    this.scene = "expedition";
    this.homePlots = this.plots;
    this.homeResourceNodes = this.resourceNodes;
    this.homePosition = { ...this.player.pos };
    this.plots = [];
    this.resourceNodes = expeditionResources(mapId);
    this.run = createRun(mapId);
    this.battleResult = "";
    this.destination = null;
    this.selectedPlotId = null;
    this.enemies = [];
    this.threats = [];
    this.bolts = [];
    this.effects = [];
    this.ghosts = [];
    if (this.possessedCompanionId !== null) this.endPossession();
    this.player.pos = { ...EXPEDITIONS[mapId].spawn };
    this.player.velocity = { x: 0, y: 0 };
    for (const [slot, c] of this.companions.entries()) {
      c.pos = formation(this.player.pos, slot);
      c.anchor = { ...c.pos };
      c.command = "follow";
      c.focusEnemyId = null;
      c.workTargetId = undefined;
      c.velocity = { x: 0, y: 0 };
    }
    // 入口守卫驻留在远方，不在传送落点贴身生成；后续遭遇由接近触发。
    this.activateEncounter("entrance");
    this.say(
      `抵达${EXPEDITIONS[mapId].name} · 点击路标探索，入口可撤离`,
      "#cce4b3",
      undefined,
      "awaken",
    );
    return true;
  }
  retreat(): void {
    this.pending = [];
    this.rushes = [];
    this.dashMotion = null;
    const wasAway = this.expedition;
    if (this.homePlots) this.plots = this.homePlots;
    if (this.homeResourceNodes) this.resourceNodes = this.homeResourceNodes;
    const returnPosition = this.homePosition ?? CAMP;
    this.homePlots = null;
    this.homeResourceNodes = null;
    this.homePosition = null;
    this.scene = "garden";
    this.run = null;
    this.expedition = false;
    this.enemies = [];
    this.threats = [];
    this.bolts = [];
    this.destination = null;
    this.player.pos = { ...returnPosition };
    this.player.velocity = { x: 0, y: 0 };
    this.effects = [];
    this.ghosts = [];
    this.player.health = Math.max(this.player.health, 40);
    if (this.possessedCompanionId !== null) this.endPossession();
    for (const c of this.companions) {
      c.pos = formation(this.player.pos, this.companions.indexOf(c));
      c.command = "follow";
      c.anchor = { ...c.pos };
      c.focusEnemyId = null;
      c.workTargetId = undefined;
      c.velocity = { x: 0, y: 0 };
    }
    this.say(wasAway ? "全队回到庭园 · 已采集资源保留" : "全队已召回安全庭园");
  }

  interactExpedition(id?: string): boolean {
    if (!this.run) return false;
    if (
      (id === "exit" || !id) &&
      distance(this.controlled.pos, this.run.exit) <= REACH + 4
    )
      return this.extractExpedition();
    const objective = id
      ? this.run.objectives.find((o) => o.id === id)
      : this.run.objectives.find(
          (o) =>
            !o.complete && distance(o.pos, this.controlled.pos) <= REACH + 4,
        );
    if (!objective || distance(this.controlled.pos, objective.pos) > REACH + 4)
      return false;
    if (objective.complete) {
      this.say("这枚路标已修复");
      return true;
    }
    const encounter = this.run.encounters.find(
      (e) => e.id === objective.encounterId,
    )!;
    if (!encounter.triggered) this.activateEncounter(encounter.id);
    if (
      encounter.enemyIds.some((id) => this.enemies.some((e) => e.id === id))
    ) {
      this.say("先清理路标附近的守卫，再修复灵脉");
      return true;
    }
    encounter.cleared = true;
    objective.complete = true;
    animate(this.controlled, "gather");
    this.effect("awaken", objective.pos, 110, this.mapDefinition!.accent, 1.1);
    this.say(
      `${objective.name}已修复 · ${this.run.objectives.filter((o) => o.complete).length}/2`,
      "#d7efb5",
      objective.pos,
      "awaken",
    );
    return true;
  }

  extractExpedition(): boolean {
    if (!this.run || distance(this.controlled.pos, this.run.exit) > REACH + 4) {
      this.say("走近金色出口才能撤离；也可使用紧急回庭园");
      return false;
    }
    const won = this.run.phase === "cleared",
      map = this.mapDefinition!;
    this.retreat();
    if (won) {
      this.performMotion(this.player, "cheer", 1);
      const harder = map.id === "ember-quarry";
      this.progress.victories++;
      this.inventory.spiritDew += harder ? 7 : 5;
      this.inventory.islandDust += harder ? 8 : 6;
      this.progress.coins += harder ? 32 : 24;
      this.seeds[harder ? "chili" : "moonherb"] += 2;
      this.battleResult = `${map.name}通关 · ${map.reward}`;
      this.say(this.battleResult, "#d3ec99", undefined, "harvest");
    } else this.battleResult = `${map.name}已撤离 · 采集保留，下次重新探索`;
    return true;
  }

  private spawnEnemy(kind: EnemyKind, pos: Vec2): Enemy {
    const health =
      kind === "brute"
        ? 155
        : kind === "sentinel"
          ? 115
          : kind === "moth"
            ? 70
            : 58;
    const enemy: Enemy = {
      id: this.nextId++,
      kind,
      pos: { ...pos },
      velocity: { x: 0, y: 0 },
      radius: kind === "brute" ? 31 : 21,
      health,
      maxHealth: health,
      speed: kind === "brute" ? 58 : 80,
      damage: kind === "brute" ? 21 : 10,
      facing: Math.PI,
      attackCooldown: 1.5,
      flash: 0,
      wobble: this.nextId,
      recovery: 0,
      stagger: 0,
    };
    confine(enemy.pos);
    this.enemies.push(enemy);
    return enemy;
  }

  private activateEncounter(id: string): void {
    const encounter = this.run?.encounters.find((e) => e.id === id);
    if (!encounter || encounter.triggered) return;
    encounter.triggered = true;
    encounter.enemyIds = encounter.kinds.map(
      (kind, i) =>
        this.spawnEnemy(kind, {
          x:
            encounter.pos.x +
            Math.cos((i * Math.PI * 2) / encounter.kinds.length) * 65,
          y:
            encounter.pos.y +
            Math.sin((i * Math.PI * 2) / encounter.kinds.length) * 65,
        }).id,
    );
  }

  private updateExpedition(): void {
    const run = this.run;
    if (!run) return;
    for (const encounter of run.encounters) {
      if (
        !encounter.triggered &&
        distance(this.controlled.pos, encounter.pos) < 280
      )
        this.activateEncounter(encounter.id);
      if (
        encounter.triggered &&
        !encounter.enemyIds.some((id) => this.enemies.some((e) => e.id === id))
      )
        encounter.cleared = true;
    }
    if (
      run.phase === "exploring" &&
      run.objectives.every((o) => o.complete) &&
      distance(this.controlled.pos, this.mapDefinition!.bossPosition) < 350
    ) {
      const id = this.mapDefinition!.bossId,
        spec = BOSSES[id];
      const boss = this.spawnEnemy("brute", this.mapDefinition!.bossPosition);
      Object.assign(boss, {
        radius: 49,
        health: spec.health,
        maxHealth: spec.health,
        damage: spec.damage,
        speed: spec.speed,
        attackCooldown: 2,
      });
      boss.boss = {
        id,
        phase: 1,
        moveIndex: 0,
        weakness: "观察蓄力 · 横向闪避",
      };
      run.bossId = boss.id;
      run.phase = "boss";
      this.say(
        `${spec.name}苏醒 · 灵潮可以打断蓄力`,
        spec.color,
        boss.pos,
        "awaken",
      );
    }
    if (run.phase === "boss" && !this.boss) {
      run.phase = "cleared";
      run.exit = { ...this.mapDefinition!.bossPosition };
      this.threats = [];
      this.bolts = this.bolts.filter((b) => b.friendly);
      this.say(
        "领主已击败 · 金色出口已开启，撤离领取奖励",
        "#f5df9d",
        run.exit,
        "awaken",
      );
    }
  }

  private updateCompanions(dt: number): void {
    const reservations = new Map<number, number>();
    for (const [slot, c] of this.companions.entries()) {
      if (c.health <= 0 || c.possessed) continue;
      c.velocity = { x: 0, y: 0 };
      c.behavior = chooseBehavior({
        controlled: c.possessed,
        frozen: statusSpeed(c) === 0,
        command: c.command,
        resource: this.resourceNodes.some((n) => n.active),
        enemy: this.enemies.some(
          (e) => distance(e.pos, c.pos) < AI_PROFILES[c.cropId].perception,
        ),
        far: distance(c.pos, this.controlled.pos) > 90,
        idle: AI_PROFILES[c.cropId].idle,
      });
      if (c.behavior === "frozen") continue;
      c.producedTimer += dt;
      if (
        c.cropId === "coconut" &&
        c.producedTimer >= 22 &&
        c.command !== "gather" &&
        !this.enemies.some((e) => distance(e.pos, c.pos) < 235)
      ) {
        c.producedTimer = 0;
        this.inventory.food++;
        this.say("椰露 +1", "#dfe4a1", c.pos, "collect");
      }
      if (
        c.cropId === "moonherb" &&
        c.producedTimer >= 2 &&
        c.command === "follow"
      ) {
        c.producedTimer = 0;
        for (const a of [this.player, ...this.companions])
          if (distance(a.pos, c.pos) < 180)
            a.health = Math.min(a.maxHealth, a.health + 5);
      }
      // 临近生效的明确预警优先于集火/采集；伙伴不需要玩家逐一微操避圈。
      const danger = this.threats.find(
        (t) =>
          t.remaining < 0.8 &&
          t.remaining > 0 &&
          t.kind !== "heal" &&
          !t.ranged &&
          (t.kind === "rush"
            ? segmentDistance(c.pos, t.source, t.pos) < t.radius + c.radius
            : distance(c.pos, t.pos) < t.radius + c.radius &&
              distance(c.pos, t.pos) > (t.innerRadius ?? 0) - c.radius),
      );
      if (danger && c.command !== "retreat") {
        c.behavior = "evade";
        let escape: Vec2;
        if (danger.kind === "rush") {
          const d = direction(danger.source, danger.pos),
            side =
              (c.pos.x - danger.source.x) * -d.y +
                (c.pos.y - danger.source.y) * d.x >=
              0
                ? 1
                : -1;
          escape = {
            x: c.pos.x - d.y * side * 120,
            y: c.pos.y + d.x * side * 120,
          };
        } else {
          const d = direction(danger.pos, c.pos);
          const inward =
            danger.innerRadius &&
            distance(c.pos, danger.pos) <
              (danger.radius + danger.innerRadius) / 2;
          const radius = inward
            ? Math.max(12, danger.innerRadius! - c.radius - 14)
            : danger.radius + c.radius + 24;
          escape = {
            x: danger.pos.x + (d.x || 1) * radius,
            y: danger.pos.y + d.y * radius,
          };
        }
        confine(escape);
        c.velocity = approach(c.pos, escape, this.speedOf(c) * 1.8, dt, 4);
        continue;
      }
      // 集火锁定同一目标，目标死亡才重新索敌；不能让每位伙伴各打最近的敌人。
      const focused =
        c.command === "focus"
          ? this.enemies.find((e) => e.id === c.focusEnemyId && e.health > 0)
          : undefined;
      const enemy =
        focused ??
        this.enemies
          .filter(
            (e) =>
              e.health > 0 &&
              distance(e.pos, c.command === "guard" ? c.anchor : c.pos) <
                (c.command === "focus"
                  ? 1200
                  : AI_PROFILES[c.cropId].perception),
          )
          .sort((a, b) => distance(a.pos, c.pos) - distance(b.pos, c.pos))[0];
      if (c.command === "retreat") {
        const rally = this.run?.exit ?? CAMP;
        c.velocity = approach(c.pos, rally, this.speedOf(c) * 1.5, dt, 35);
        if (!this.run && distance(c.pos, rally) < 70)
          c.health = Math.min(c.maxHealth, c.health + dt * 3);
        continue;
      }
      if (enemy && c.command !== "gather") {
        c.behavior = "engage";
        const ranged = c.cropId === "carrot" || c.cropId === "chili",
          range = ranged ? 220 : 78;
        const angle = slot * Math.PI * 0.25;
        const attackSlot = {
          x: enemy.pos.x + Math.cos(angle) * (ranged ? 180 : 55),
          y: enemy.pos.y + Math.sin(angle) * (ranged ? 180 : 55),
        };
        if (distance(c.pos, enemy.pos) > range)
          c.velocity = approach(
            c.pos,
            attackSlot,
            this.speedOf(c) * 1.2,
            dt,
            5,
          );
        else if (c.attackCooldown <= 0) {
          c.facing = Math.atan2(enemy.pos.y - c.pos.y, enemy.pos.x - c.pos.x);
          c.attackCooldown = 0.72;
          animate(c, "attack");
          this.performMotion(c, "attack", 0.4, 4 / 30);
          this.pending.push({
            remaining: 4 / 30,
            actor: c,
            resolve: () => {
              if (enemy.health <= 0) return;
              if (ranged) {
                const d = direction(c.pos, enemy.pos);
                this.bolts.push({
                  kind: "seed",
                  pos: { ...c.pos },
                  velocity: { x: d.x * 430, y: d.y * 430 },
                  life: 0.8,
                  damage: c.damage,
                  friendly: true,
                  status: c.cropId === "chili" ? "burn" : undefined,
                });
              } else if (distance(c.pos, enemy.pos) < 100)
                this.hitEnemy(enemy, c.damage, c.pos);
            },
          });
        }
        continue;
      }
      if (c.command === "gather") {
        c.behavior = "gather";
        const work = CROPS[c.cropId].workKind;
        const node = this.resourceNodes
          .filter((n) => n.active)
          .sort(
            (a, b) =>
              distance(a.pos, c.pos) +
              (work && a.kind !== work ? 400 : 0) +
              (reservations.get(a.id) ?? 0) * 600 -
              (a.id === c.workTargetId ? 100 : 0) -
              (distance(b.pos, c.pos) +
                (work && b.kind !== work ? 400 : 0) +
                (reservations.get(b.id) ?? 0) * 600 -
                (b.id === c.workTargetId ? 100 : 0)),
          )[0];
        if (node) {
          c.workTargetId = node.id;
          reservations.set(node.id, (reservations.get(node.id) ?? 0) + 1);
          if (distance(c.pos, node.pos) > 70)
            c.velocity = approach(c.pos, node.pos, this.speedOf(c), dt, 65);
          else this.chop(node, c);
          continue;
        }
      }
      const anchor =
        c.command === "guard" ? c.anchor : formation(this.controlled.pos, slot);
      const catchup =
        distance(c.pos, anchor) > 180
          ? Math.max(c.speed * 1.6, this.controlled.speed * 1.2)
          : c.speed;
      c.velocity = approach(c.pos, anchor, catchup * statusSpeed(c), dt, 12);
      if (Math.hypot(c.velocity.x, c.velocity.y) > 5)
        c.facing = Math.atan2(c.velocity.y, c.velocity.x);
    }
  }

  private hitEnemy(e: Enemy, damage: number, source?: Vec2): void {
    if (e.health <= 0) return;
    if (source === this.player.pos)
      damage *= equipmentStats(this.equipment).power;
    if (
      e.kind === "sentinel" &&
      source &&
      !(e.recovery ?? 0) &&
      !(e.stagger ?? 0)
    ) {
      const d = direction(e.pos, source);
      if (d.x * Math.cos(e.facing) + d.y * Math.sin(e.facing) > 0.25) {
        damage = Math.max(1, Math.round(damage * 0.25));
        this.say("格挡 · 绕背/打断", "#edcf83", e.pos);
      }
    }
    if (e.boss)
      damage = Math.round(damage * ((e.recovery ?? 0) > 0 ? 1.8 : 0.7));
    else if ((e.recovery ?? 0) > 0) damage = Math.round(damage * 1.4);
    e.health -= damage;
    this.effect("impact", e.pos, e.radius + 16, "#fff0ba", 0.24);
    e.flash = 0.16;
    // 首领蓄力保持动作可读性，受击闪光不能反复覆盖已承诺的起手动作。
    if (!e.boss || !this.threats.some((t) => t.id === e.id)) animate(e, "hurt");
    this.say(`-${Math.round(damage)}`, "#f5c4a6", e.pos, "hit");
    if (e.health <= 0) {
      this.ghosts.push({
        art: e.boss?.id ?? e.kind,
        pos: { ...e.pos },
        age: 0,
        duration: 8 / 12,
        facing: e.facing,
        clip: "death",
      });
      this.progress.coins += 2;
      this.inventory.islandDust++;
      this.enemies = this.enemies.filter((n) => n.id !== e.id);
    }
  }

  private updateEnemies(dt: number): void {
    for (const e of this.enemies) {
      e.flash = Math.max(0, e.flash - dt);
      e.attackCooldown = Math.max(0, e.attackCooldown - dt);
      e.recovery = Math.max(0, (e.recovery ?? 0) - dt);
      e.stagger = Math.max(0, (e.stagger ?? 0) - dt);
      e.velocity = { x: 0, y: 0 };
      e.home ??= { ...e.pos };
      if (statusSpeed(e) === 0) {
        e.behavior = "frozen";
        continue;
      }
      if (e.stagger > 0 && e.motion?.id !== "stagger")
        this.performMotion(e, "stagger", e.stagger);
      if (e.boss) {
        const phase = bossPhase(e.health / e.maxHealth);
        if (phase !== e.boss.phase) {
          e.boss.phase = phase;
          this.say(
            `${BOSSES[e.boss.id].name} · 第 ${phase} 阶段`,
            BOSSES[e.boss.id].color,
            e.pos,
            "awaken",
          );
          this.effect("ring", e.pos, 150, BOSSES[e.boss.id].color, 0.8);
        }
        e.boss.weakness =
          e.recovery > 0 ? "核心暴露 · 伤害 ×1.8" : "躲开预警 · 用灵潮打断蓄力";
      }
      if (e.recovery > 0 || e.stagger > 0) continue;
      const target = [this.player, ...this.companions]
        .filter((a) => a.health > 0)
        .sort((a, b) => distance(a.pos, e.pos) - distance(b.pos, e.pos))[0];
      if (!target) continue;
      if (this.threats.some((t) => t.id === e.id)) continue;
      if (e.boss) {
        this.updateBoss(e, target, dt);
        continue;
      }
      // 不同区域的守卫不会跨整张地图追击传送落点。
      if (
        distance(e.pos, target.pos) > AI_PROFILES[e.kind].perception ||
        distance(e.pos, e.home) > AI_PROFILES[e.kind].leash
      ) {
        e.behavior =
          distance(e.pos, e.home) < 15 ? AI_PROFILES[e.kind].idle : "return";
        e.velocity = approach(e.pos, e.home, this.speedOf(e), dt, 12);
        continue;
      }
      e.behavior = "engage";
      const spec = ENEMIES[e.kind];
      const range = spec.range;
      if (distance(e.pos, target.pos) > range) {
        e.velocity = approach(e.pos, target.pos, this.speedOf(e), dt, range);
        e.facing = Math.atan2(target.pos.y - e.pos.y, target.pos.x - e.pos.x);
      } else if (e.attackCooldown <= 0) {
        e.facing = Math.atan2(target.pos.y - e.pos.y, target.pos.x - e.pos.x);
        const duration = spec.windup;
        const healing =
          e.kind === "moth" &&
          this.enemies.some(
            (a) =>
              a.id !== e.id &&
              a.health < a.maxHealth &&
              distance(a.pos, e.pos) < 260,
          );
        this.threats.push({
          kind: healing
            ? "heal"
            : e.kind === "mite" || e.kind === "sentinel"
              ? "rush"
              : e.kind === "brute"
                ? "slam"
                : "volley",
          id: e.id,
          pos: { ...(healing ? e.pos : target.pos) },
          source: { ...e.pos },
          radius: healing
            ? 260
            : e.kind === "brute"
              ? e.elite
                ? 125
                : 100
              : 35,
          remaining: duration,
          duration,
          damage: e.damage,
          ranged: !healing && (e.kind === "spitter" || e.kind === "moth"),
        });
        e.attackCooldown = duration + 1.1;
        this.signals.push({ text: "", color: "", sound: "warning" });
        animate(e, "attack");
        e.animation!.duration = duration;
        this.performMotion(
          e,
          healing
            ? "heal"
            : e.kind === "mite" || e.kind === "sentinel"
              ? "rush"
              : e.kind === "brute"
                ? "slam"
                : "volley",
          duration + 0.35,
          duration,
        );
      } else if (e.kind === "spitter" && distance(e.pos, target.pos) < 170) {
        const away = direction(target.pos, e.pos);
        e.velocity = approach(
          e.pos,
          { x: e.pos.x + away.x * 90, y: e.pos.y + away.y * 90 },
          this.speedOf(e) * 0.8,
          dt,
        );
      }
    }
  }

  private updateBoss(
    enemy: Enemy,
    target: Player | Companion,
    dt: number,
  ): void {
    const state = enemy.boss!;
    enemy.facing = Math.atan2(
      target.pos.y - enemy.pos.y,
      target.pos.x - enemy.pos.x,
    );
    if (distance(enemy.pos, target.pos) > 400) {
      enemy.velocity = approach(
        enemy.pos,
        target.pos,
        this.speedOf(enemy),
        dt,
        350,
      );
      return;
    }
    if (enemy.attackCooldown > 0) return;
    const move = bossMove(state);
    state.moveIndex++;
    const duration = state.phase === 3 ? 1.1 : 1.4;
    const base: Threat = {
      id: enemy.id,
      source: { ...enemy.pos },
      pos: { ...target.pos },
      remaining: duration,
      duration,
      radius: 50,
      damage: enemy.damage,
      ranged: false,
    };
    if (move === "lunge") {
      // 冲撞锁定线段，目标再移动不会追踪修正终点。
      const d = direction(enemy.pos, target.pos);
      const reach = Math.min(300, distance(enemy.pos, target.pos) + 65);
      const end = {
        x: enemy.pos.x + d.x * reach,
        y: enemy.pos.y + d.y * reach,
      };
      confine(end);
      this.threats.push({
        ...base,
        kind: "rush",
        pos: end,
        radius: 48,
        label: "荆角突刺 · 横向闪避",
      });
    } else if (move === "roots") {
      const spread = state.phase === 1 ? 90 : 120;
      for (let i = 0; i < 3; i++)
        this.threats.push({
          ...base,
          kind: "slam",
          pos: {
            x: target.pos.x + (i - 1) * spread,
            y: target.pos.y + (i === 1 ? 0 : 45),
          },
          radius: state.phase === 3 ? 80 : 65,
          remaining: duration + i * 0.2,
          duration: duration + i * 0.2,
          label: "根蔓连爆 · 离开锁定落点",
        });
    } else if (move === "ring") {
      this.threats.push({
        ...base,
        kind: "ring",
        pos: { ...enemy.pos },
        radius: 285,
        innerRadius: 110,
        duration: 1.7,
        remaining: 1.7,
        label: "外环震荡 · 靠近核心安全",
      });
    } else {
      this.threats.push({
        ...base,
        kind: "volley",
        ranged: true,
        radius: 35,
        label: "熔晶吐息 · 绕开扇形",
      });
    }
    enemy.attackCooldown = duration + 2.6;
    animate(enemy, "attack");
    enemy.animation!.duration =
      move === "ring" ? 1.7 : move === "roots" ? duration + 0.4 : duration;
    this.performMotion(
      enemy,
      move === "lunge" ? "rush" : move === "breath" ? "volley" : "slam",
      enemy.animation!.duration + 0.35,
      enemy.animation!.duration,
    );
    this.say(
      this.threats[this.threats.length - 1].label!,
      BOSSES[state.id].color,
      enemy.pos,
    );
  }

  private updateThreats(dt: number): void {
    for (const t of this.threats) {
      t.remaining -= dt;
      const enemy = this.enemies.find((e) => e.id === t.id);
      if (enemy && (statusSpeed(enemy) === 0 || (enemy.stagger ?? 0) > 0)) {
        t.remaining = 0;
        continue;
      }
      if (t.remaining > 0 || !enemy) continue;
      enemy.recovery = enemy.boss ? 2.2 : ENEMIES[enemy.kind].recovery;
      if (!enemy.motion) animate(enemy, "attack");
      if (t.kind === "heal") {
        for (const ally of this.enemies)
          if (ally.id !== enemy.id && distance(ally.pos, enemy.pos) < 260)
            ally.health = Math.min(ally.maxHealth, ally.health + 28);
        this.effect("ring", enemy.pos, 260, "#b5caff", 0.6);
        this.say("织雾治疗 +28", "#c6d7ff", enemy.pos, "heal");
      } else if (t.kind === "rush") {
        this.effect(
          "dash",
          t.source,
          distance(t.source, t.pos),
          "#ff9a91",
          0.3,
          Math.atan2(t.pos.y - t.source.y, t.pos.x - t.source.x),
        );
        this.rushes.push({
          enemy,
          source: { ...t.source },
          end: { ...t.pos },
          age: 0,
          radius: t.radius,
          damage: t.damage,
          hit: new Set(),
        });
        this.performMotion(enemy, "rush", 0.24);
      } else if (t.ranged) {
        const d = direction(t.source, t.pos);
        for (const spread of [-0.24, 0, 0.24])
          this.bolts.push({
            kind: "spore",
            pos: { ...t.source },
            velocity: {
              x: (d.x * Math.cos(spread) - d.y * Math.sin(spread)) * 210,
              y: (d.x * Math.sin(spread) + d.y * Math.cos(spread)) * 210,
            },
            life: 2.3,
            damage: t.damage,
            friendly: false,
          });
      } else {
        this.effect("ring", t.pos, t.radius, "#ff9682", 0.5);
        for (const a of [this.player, ...this.companions])
          if (
            distance(a.pos, t.pos) < t.radius &&
            distance(a.pos, t.pos) >= (t.innerRadius ?? 0)
          )
            this.hurt(a, t.damage);
      }
    }
    this.threats = this.threats.filter(
      (t) => t.remaining > 0 && this.enemies.some((e) => e.id === t.id),
    );
    for (const bolt of this.bolts) {
      const previous = { ...bolt.pos };
      bolt.life -= dt;
      bolt.pos.x += bolt.velocity.x * dt;
      bolt.pos.y += bolt.velocity.y * dt;
      if (bolt.friendly) {
        const enemies = this.enemies
          .filter(
            (e) =>
              !bolt.hitIds?.includes(e.id) &&
              segmentDistance(e.pos, previous, bolt.pos) <
                e.radius + (bolt.kind === "lance" ? 18 : 10),
          )
          .sort(
            (a, b) => distance(a.pos, previous) - distance(b.pos, previous),
          );
        for (const enemy of enemies) {
          this.hitEnemy(enemy, bolt.damage, {
            x: bolt.pos.x - bolt.velocity.x * 0.1,
            y: bolt.pos.y - bolt.velocity.y * 0.1,
          });
          if (bolt.status) applyStatus(enemy, bolt.status);
          (bolt.hitIds ??= []).push(enemy.id);
          bolt.pierce = (bolt.pierce ?? 1) - 1;
          if (bolt.pierce <= 0) {
            bolt.life = 0;
            break;
          }
        }
      } else {
        const ally = [this.player, ...this.companions].find(
          (a) => segmentDistance(a.pos, previous, bolt.pos) < a.radius + 8,
        );
        if (ally) {
          const hit = this.hurt(ally, bolt.damage);
          if (hit && !this.deathFrame && bolt.kind === "spore")
            applyStatus(ally, "poison");
          bolt.life = 0;
        }
      }
    }
    this.bolts = this.bolts.filter((b) => b.life > 0);
  }
  hurt(actor: Player | Companion, damage: number): boolean {
    if (this.deathFrame || actor.health <= 0) return false;
    if (actor === this.controlled && this.dodge > 0) return false;
    if (actor === this.player && this.player.invulnerable > 0) return false;
    if (actor === this.player)
      damage = Math.max(
        1,
        Math.round(damage * (1 - equipmentStats(this.equipment).defense)),
      );
    if (actor === this.controlled && this.guardRemaining > 0)
      damage = Math.max(1, Math.round(damage * 0.5));
    actor.health -= damage;
    this.pending = this.pending.filter((a) => a.actor !== actor);
    actor.flash = 0.2;
    animate(actor, "hurt");
    if (actor === this.player) this.player.invulnerable = 0.45;
    this.say(`-${damage}`, "#efa895", actor.pos, "hurt");
    if (actor.health > 0) return true;
    if ("cropId" in actor) {
      if (actor.reviveWard) {
        actor.reviveWard = false;
        actor.health = actor.maxHealth * 0.55;
        actor.pos = { ...(this.run?.exit ?? CAMP) };
        actor.command = "retreat";
        this.say(`${actor.name} 的续生印保住了生命`);
        return true;
      }
      if (actor.possessed) this.endPossession();
      this.ghosts.push({
        art: actor.cropId,
        pos: { ...actor.pos },
        age: 0,
        duration: 8 / 12,
        facing: actor.facing,
        clip: "death",
      });
      this.spoilingFood.push({
        id: this.nextId++,
        amount: 2,
        remaining: 70,
        sourceName: actor.name,
      });
      this.companions = this.companions.filter((c) => c.id !== actor.id);
      if (this.selectedCompanionId === actor.id)
        this.selectedCompanionId = this.companions[0]?.id ?? null;
      this.say(
        `${actor.name} 陨落 · 易腐食物恢复 14 点，70 秒后腐坏`,
        "#efa895",
      );
    } else {
      this.deathFrame = true;
      this.ghosts.push({
        art: "hero",
        pos: { ...actor.pos },
        age: 0,
        duration: 8 / 12,
        facing: actor.facing,
        clip: "death",
      });
      this.retreat();
      this.player.health = 70;
      this.progress.coins = Math.max(0, this.progress.coins - 3);
      this.say("灵脉送你回到庭园 · 铜叶 -3，种植进度保留", "#efa895");
    }
    return true;
  }

  private effect(
    kind: CombatEffect["kind"],
    pos: Vec2,
    radius: number,
    color: string,
    duration: number,
    angle = 0,
  ): void {
    this.effects.push({
      kind,
      pos: { ...pos },
      radius,
      color,
      duration,
      life: duration,
      angle,
    });
  }
}
